import React, { useCallback, useState } from 'react';

// =============================================================================
// 5.43.0 calendar windows close ONLY by their buttons (Henry 2026-11-02): clicking outside a window does nothing and
// Esc does nothing; Cancel / Save (or the window's main action) / the ✕ close it. When the user changed something,
// Cancel / ✕ first ask "Discard changes?" (Discard / Keep editing); nothing changed = no question.
// Used by the event window, the follow-up dialog, the Tasks editor, the notes reader and the .ics import window.
// =============================================================================

export function CloseX({ onClick, disabled, testid = 'window-x', color = '#aaa' }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} aria-label="Close" title="Close" data-testid={testid}
      style={{ background: 'transparent', border: 'none', color, fontSize: 20, lineHeight: 1, cursor: disabled ? 'default' : 'pointer',
        padding: '2px 6px', borderRadius: 6, opacity: disabled ? 0.5 : 1, flexShrink: 0 }}>✕</button>
  );
}

export function DiscardPrompt({ onDiscard, onKeep }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2600, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div role="alertdialog" aria-label="Discard changes?" data-testid="discard-prompt"
        style={{ background: '#242424', color: '#f0f0f0', border: '1px solid #3a3a3a', borderRadius: 10, padding: '18px 20px', width: 320, maxWidth: '92vw', boxShadow: '0 10px 40px rgba(0,0,0,0.5)' }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>Discard changes?</div>
        <div style={{ fontSize: 13, color: '#aaa', marginBottom: 16 }}>Your changes in this window have not been saved.</div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" onClick={onKeep} data-testid="discard-keep" autoFocus
            style={{ padding: '7px 14px', border: '1px solid #3a3a3a', borderRadius: 6, background: 'transparent', color: '#f0f0f0', fontSize: 13, cursor: 'pointer' }}>Keep editing</button>
          <button type="button" onClick={onDiscard} data-testid="discard-yes"
            style={{ padding: '7px 14px', border: 'none', borderRadius: 6, background: '#b91c1c', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Discard</button>
        </div>
      </div>
    </div>
  );
}

// requestClose(): closes at once when nothing changed, else shows the prompt. Render `prompt` inside the window.
export function useCloseGuard(dirty, onClose) {
  const [asking, setAsking] = useState(false);
  const requestClose = useCallback(() => { if (dirty) setAsking(true); else onClose(); }, [dirty, onClose]);
  const prompt = asking
    ? <DiscardPrompt onKeep={() => setAsking(false)} onDiscard={() => { setAsking(false); onClose(); }} />
    : null;
  return { requestClose, prompt };
}
